import { ReactNode, createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface LSContextModel {
  lastSectionId: string;
  updateLastSectionId: (sectionId: string) => void;
  removeLastSectionNumber: () => void;
  lastSectionNumber: string;
  lastSectionCache: string | null;
  updateLastSectionNumber: (sectionNumber: string) => void;
  getLastSectionOpenned: () => string | null;
  removeLastSectionOpenned: () => void;
  //
  lastLayer: string;
  updateLastLayer: (lastLayer: string) => void;
  getLastLayerChecked: () => string | null;
  removeLastLayerChecked: () => void;
  //
  lastRoundId: string;
  updateLastRoundId: (lastRoundId: string) => void;
  getLastRoundId: () => string | null;
  removeLastRoundId: () => void;
  //
  lastHoldNumber: string;
  updateLastHoldNumber: (lastRoundId: string) => void;
  getLastHoldNumber: () => string | null;
  removeLastHoldNumber: () => void;
}

const LAST_SECTION_ID = 'last-section-id';
const LAST_SECTION_NUMBER = 'last-section-number';
const LAST_LAYER = 'last-layer';
const LAST_ROUND_ID = 'last-round-id';
const LAST_HOLD_NUMBER = 'last-hold-number';

interface LSContextProviderProps {
  children: ReactNode;
}

export const LSContext = createContext<LSContextModel | undefined>(undefined);

export const LSProvider: React.FC<LSContextProviderProps> = ({ children }) => {
  const [lastSectionId, setLastSectionId] = useState(localStorage.getItem(LAST_SECTION_ID) ?? '');
  const [lastSectionNumber, setLastSectionNumber] = useState(
    localStorage.getItem(LAST_SECTION_NUMBER) ?? ''
  );
  const [lastLayer, setLastLayer] = useState(localStorage.getItem(LAST_LAYER) ?? '');
  const [lastRoundId, setLastRoundId] = useState(localStorage.getItem(LAST_ROUND_ID) ?? '');
  const [lastHoldNumber, setLastHoldNumber] = useState('');

  const location = useLocation();
  const inStowageDetailsView = location.pathname.includes('/stowagePlan/');

  ////////////////////////////////

  const lastSectionCache = localStorage.getItem(LAST_SECTION_NUMBER);

  const getLastSectionOpenned = () => localStorage.getItem(LAST_SECTION_ID);

  const getLastSectionNumber = () => localStorage.getItem(LAST_SECTION_NUMBER);

  const setLastSectionOpenned = (value: string) => localStorage.setItem(LAST_SECTION_ID, value);

  const removeLastSectionOpenned = () => {
    localStorage.removeItem(LAST_SECTION_ID);
  };
  const removeLastSectionNumber = () => {
    localStorage.removeItem(LAST_SECTION_NUMBER);
  };

  const updateLastSectionId = (sectionId: string) => setLastSectionId(sectionId);
  const updateLastSectionNumber = (sectionNumber: string) =>
    localStorage.setItem(LAST_SECTION_NUMBER, sectionNumber);
  // setLastSectionNumber(sectionNumber.toString());

  ////////////////////////////////

  const getLastLayerChecked = () => localStorage.getItem(LAST_LAYER);

  const setLastLayerChecked = (value: string) => localStorage.setItem(LAST_LAYER, value);

  const removeLastLayerChecked = () => {
    localStorage.removeItem(LAST_LAYER);
  };

  const updateLastLayer = (lastLayer: string) => {
    setLastLayer(lastLayer);
  };

  ////////////////////////////////

  const getLastRoundId = () => localStorage.getItem(LAST_ROUND_ID);

  const setRoundId = (value: string) => localStorage.setItem(LAST_ROUND_ID, value);

  const removeLastRoundId = () => {
    localStorage.removeItem(LAST_ROUND_ID);
  };

  const updateLastRoundId = (roundId: string) => {
    setLastRoundId(roundId);
  };

  ////////////////////////////////

  const getLastHoldNumber = () => localStorage.getItem(LAST_HOLD_NUMBER);

  const setHoldNumber = (value: string) => localStorage.setItem(LAST_HOLD_NUMBER, value);

  const removeLastHoldNumber = () => {
    localStorage.removeItem(LAST_HOLD_NUMBER);
  };
  const updateLastHoldNumber = (roundId: string) => {
    setLastHoldNumber(roundId);
  };

  const contextData: LSContextModel = {
    lastSectionId,
    updateLastSectionId,
    lastSectionNumber,
    lastSectionCache,
    updateLastSectionNumber,
    getLastSectionOpenned,
    removeLastSectionOpenned,
    removeLastSectionNumber,
    lastLayer,
    updateLastLayer,
    getLastLayerChecked,
    removeLastLayerChecked,
    lastRoundId,
    updateLastRoundId,
    getLastRoundId,
    removeLastRoundId,
    lastHoldNumber,
    updateLastHoldNumber,
    getLastHoldNumber,
    removeLastHoldNumber
  };

  useEffect(() => {
    const localValue = getLastSectionOpenned();
    if (localValue !== lastSectionId) setLastSectionOpenned(lastSectionId);
  }, [lastSectionId]);

  useEffect(() => {
    const localValue = getLastSectionNumber();
    if (localValue !== lastSectionNumber) setLastSectionNumber(lastSectionNumber);
  }, [lastSectionNumber]);

  useEffect(() => {
    const localValue = getLastLayerChecked();
    if (localValue != lastLayer) setLastLayerChecked(lastLayer);
  }, [lastLayer]);

  useEffect(() => {
    const localValue = getLastRoundId();
    if (localValue != lastRoundId) setRoundId(lastRoundId);
  }, [lastRoundId]);

  useEffect(() => {
    const localValue = getLastHoldNumber();
    if (localValue != lastHoldNumber) setHoldNumber(lastHoldNumber);
  }, [lastHoldNumber]);

  useEffect(() => {
    if (!inStowageDetailsView) {
      removeLastRoundId();
      setLastRoundId('');
      setLastSectionNumber('');
      removeLastLayerChecked();
      setLastLayerChecked('');
      removeLastHoldNumber();
      setLastHoldNumber('');
    }
  }, [inStowageDetailsView]);

  return <LSContext.Provider value={contextData}>{children}</LSContext.Provider>;
};
